import {useRefreshTokenMutation} from 'api/authSlice';
import {useEffect, useRef} from 'react';
import {useSelector, useStore} from 'react-redux';
import {selectIsLoggedIn} from 'store/authSlice';
import {handleReauthFailure} from 'utils/reauthFailure';

const REFRESH_INTERVAL_MS = 25 * 60 * 1000; // Refresh every 25 minutes

export function useTokenRefresh() {
  const [refreshToken] = useRefreshTokenMutation();
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const store = useStore();
  const isLoggedInRef = useRef(isLoggedIn);
  const timerRef = useRef(null);
  const hasShownSessionExpiredRef = useRef(false);

  useEffect(() => {
    isLoggedInRef.current = isLoggedIn;
  }, [isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn) return;

    const scheduleRefresh = async () => {
      // isLoggedIn may have flipped to false (e.g. a concurrent /auth/me/
      // 401 already logged us out and ProtectedRoute already surfaced the
      // auth prompt) between when this was scheduled and now — skip firing
      // a redundant refresh in that case.
      if (!isLoggedInRef.current) return;

      console.log('useTokenRefresh - attempting token refresh');
      try {
        await refreshToken().unwrap();
        console.log('useTokenRefresh - token refreshed');
        hasShownSessionExpiredRef.current = false;
        timerRef.current = setTimeout(scheduleRefresh, REFRESH_INTERVAL_MS);
      } catch (error) {
        const isNetworkFailure =
          error?.status === 'FETCH_ERROR' ||
          error?.status === 0 ||
          error?.name === 'NetworkError';
        if (isNetworkFailure) {
          console.log('useTokenRefresh - offline, will retry on next interval');
          timerRef.current = setTimeout(scheduleRefresh, REFRESH_INTERVAL_MS);
          return;
        }
        if (!isLoggedInRef.current) {
          // Already logged out via another path (e.g. /auth/me/ 401) —
          // ProtectedRoute already surfaced the auth prompt for this.
          console.log(
            'useTokenRefresh - refresh failed, already logged out elsewhere'
          );
          return;
        }

        console.log(
          'useTokenRefresh - refresh failed, re-authenticating',
          error
        );

        // Same recovery as a failed reactive refresh in baseQueryWithReauth:
        // silent re-login via OneLogin, or a reconnect prompt if there's
        // unsaved work.
        if (!hasShownSessionExpiredRef.current) {
          handleReauthFailure(store.getState);
          hasShownSessionExpiredRef.current = true;
        }

        timerRef.current = setTimeout(scheduleRefresh, REFRESH_INTERVAL_MS);
      }
    };

    timerRef.current = setTimeout(scheduleRefresh, REFRESH_INTERVAL_MS);

    return () => clearTimeout(timerRef.current);
  }, [isLoggedIn, refreshToken, store]);
}
